import React, { useState, useEffect } from 'react';
import {
  Mail,
  Lock,
  LogIn,
  Loader2,
  Globe,
  Crown,
  Eye,
  EyeOff,
  KeyRound,
  Users,
  ArrowLeft,
  CheckCircle2,
  TreePine,
  Shield,
  Library,
  BarChart3,
  User,
} from 'lucide-react';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { PecktreeLogo } from '../shared/PecktreeLogo.tsx';
import { Input, Label, Button } from '../ui';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface AuthScreenProps {
  onSuccess: (user: any) => void;
}

/**
 * Beginstand uit de URL (#651). De marketingsite op pecktree.nl stuurt bezoekers
 * hierheen met een bedoeling: "Start gratis" hoort op het registratieformulier uit
 * te komen en niet op inloggen, en het e-mailadres dat iemand in het inlogmenu
 * typte hoort al ingevuld te staan. Zonder dit begon élke bezoeker op `login` met
 * een leeg veld — twaalf knoppen die iets anders beloofden dan ze deden.
 */
export function initialAuthState(): { mode: 'login' | 'register' | 'forgot'; email: string } {
  if (typeof window === 'undefined') return { mode: 'login', email: '' };
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname.replace(/\/+$/, '');
  const wanted = params.get('mode');

  const mode =
    path === '/register' || wanted === 'register'
      ? 'register'
      : wanted === 'forgot'
        ? 'forgot'
        : 'login';

  // Alleen overnemen als het op een adres lijkt — de parameter komt van buiten.
  const raw = (params.get('email') || '').trim();
  const email = raw.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw) ? raw : '';

  return { mode, email };
}

const AuthScreen: React.FC<AuthScreenProps> = ({ onSuccess }) => {
  const { t, lang } = useTranslation();
  const initial = initialAuthState();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(initial.mode);
  const [email, setEmail] = useState(initial.email);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  // #1403 — activatiemail opnieuw versturen. `canResend` gaat aan zodra een
  // inlogpoging een niet-geactiveerd account meldt, of zodra iemand een minuut
  // op het successcherm staat zonder mail.
  const [canResend, setCanResend] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [googleClientId, setGoogleClientId] = useState<string>('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [inviteInfo, setInviteInfo] = useState<{
    project_name: string;
    inviter_name: string;
    invitee_email: string;
    role: string;
    invitee_exists?: boolean;
  } | null>(null);

  // Check for pending invitation and fetch context
  useEffect(() => {
    const pendingInvite = localStorage.getItem('pending_invite_params');
    if (!pendingInvite) return;
    const token = new URLSearchParams(pendingInvite).get('token');
    if (!token) return;
    fetch(`/api/invitations/${encodeURIComponent(token)}/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) {
          setInviteInfo(info);
          if (info.invitee_email) setEmail(info.invitee_email);
          // #500 — bestaande gebruiker meteen naar inlog-modus, anders register
          setMode(info.invitee_exists ? 'login' : 'register');
        } else {
          // Token onbekend/verlopen → val terug op register (oude gedrag)
          setMode('register');
        }
      })
      .catch((err) => {
        console.warn('fetch invitation info failed:', err.message || err);
        setMode('register');
      });
  }, []);

  // #1403 — de mail gaat nu via een wachtrij, dus even geduld is normaal. Pas
  // na een minuut bieden we "opnieuw versturen" aan; eerder zou het mensen
  // aanzetten tot dubbel klikken terwijl de eerste mail nog onderweg is.
  useEffect(() => {
    if (!registrationSuccess) return;
    const timer = setTimeout(() => setCanResend(true), 60_000);
    return () => clearTimeout(timer);
  }, [registrationSuccess]);

  /**
   * Foutmelding voor de gebruiker. Een ApiError kan een `messageKey` dragen —
   * dan wint de vertaling boven de Engelse servertekst. Zo komt de afgebroken
   * fetch uit #1402 als nette zin binnen in plaats van als "aborted".
   */
  const errorText = (err: { messageKey?: string; message?: string }): string => {
    if (err.messageKey) {
      const translated = t(err.messageKey);
      if (translated && translated !== err.messageKey) return translated;
    }
    return err.message || t('authError');
  };

  /** Vraag een nieuwe activatiemail aan (#1403). */
  const handleResendVerification = async () => {
    setResendState('sending');
    try {
      await ApiService.resendVerification(email);
    } catch {
      // Het antwoord is met opzet altijd hetzelfde; een fout hier zegt niets
      // over het account en mag de gebruiker niet op een spoor zetten.
    }
    setResendState('done');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === 'forgot') {
        await ApiService.forgotPassword(email);
        setForgotPasswordSent(true);
      } else if (mode === 'register') {
        const pendingInvite = localStorage.getItem('pending_invite_params');
        const inviteToken = pendingInvite
          ? new URLSearchParams(pendingInvite).get('token')
          : undefined;
        await ApiService.register({
          email,
          password,
          firstName: firstName.trim() || undefined,
          invitationToken: inviteToken || undefined,
          language: lang,
        });
        setRegistrationSuccess(true);
      } else {
        const response = await ApiService.login({ email, password });
        if (response.requires2FA) {
          // De 2FA-challenge-token zit in een httpOnly cookie; we tonen alleen de code-stap.
          setRequires2FA(true);
        } else {
          onSuccess(response.user);
        }
      }
    } catch (err: any) {
      setError(errorText(err));
      // #1403 — hier liep een gebruiker met een niet-aangekomen activatiemail
      // vast: registreren gaf "al geregistreerd", inloggen dit. Nu staat er
      // een uitweg onder de melding.
      if (err.code === 'ACCOUNT_NOT_ACTIVATED') {
        setCanResend(true);
        setResendState('idle');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.validate2FA(totpCode);
      onSuccess(response.user);
    } catch (err: any) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credential: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.loginWithGoogle(credential, lang);
      if (response.requires2FA) {
        setRequires2FA(true);
      } else {
        onSuccess(response.user);
      }
    } catch (err: any) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    fetch('/api/config')
      .then((res) => res.json())
      .then((config) => {
        if (mounted && config.googleClientId) {
          setGoogleClientId(config.googleClientId);
        }
      })
      .catch((err) => console.warn('fetch config (google client id) failed:', err.message || err));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!googleClientId) return;
    let mounted = true;

    const initGSI = async () => {
      // Dynamically load Google Sign-In script if not already present
      if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        document.head.appendChild(script);
      }
      await new Promise<void>((resolve) => {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }
        const interval = setInterval(() => {
          if (window.google?.accounts?.id) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(interval);
          resolve();
        }, 5000);
      });

      if (!window.google?.accounts?.id || !mounted) return;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response: any) => {
          if (response.credential) {
            handleGoogleLogin(response.credential);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      requestAnimationFrame(() => {
        const buttonContainer = document.getElementById('google-signin-button');
        if (buttonContainer && window.google?.accounts?.id) {
          window.google.accounts.id.renderButton(buttonContainer, {
            theme: 'outline',
            size: 'large',
            width: buttonContainer.offsetWidth,
            text: 'signin_with',
            shape: 'pill',
            logo_alignment: 'left',
          });
        }
      });
    };

    initGSI();
    return () => {
      mounted = false;
    };
  }, [googleClientId, mode]);

  const features = [
    { icon: TreePine, titleKey: 'neuralConnectivity', descKey: 'neuralConnectivityDesc' },
    { icon: Library, titleKey: 'archiveResearch', descKey: 'archiveResearchDesc' },
    { icon: BarChart3, titleKey: 'd3Precision', descKey: 'd3PrecisionDesc' },
    { icon: Shield, titleKey: 'euDataSov', descKey: 'euDataSovDesc' },
  ];

  const switchMode = (newMode: 'login' | 'register' | 'forgot') => {
    setMode(newMode);
    setError(null);
    setRegistrationSuccess(false);
    setForgotPasswordSent(false);
    setCanResend(false);
    setResendState('idle');
    if (newMode !== 'register') setFirstName('');
  };

  /* ── Success / confirmation states ─────────────────────────────────────── */
  /**
   * "Geen e-mail ontvangen? Stuur opnieuw" (#1403). Staat zowel op het
   * successcherm na registratie als onder de melding dat een account nog niet
   * geactiveerd is — dat zijn precies de twee momenten waarop iemand op een
   * mail zit te wachten die er niet is.
   */
  const renderResend = () => {
    if (!canResend) return null;
    if (resendState === 'done')
      return <p className="auth-resend-done">{t('resendVerificationDone')}</p>;
    return (
      <p className="auth-resend">
        {t('resendVerificationPrompt')}{' '}
        <button
          type="button"
          onClick={handleResendVerification}
          disabled={resendState === 'sending'}
          className="auth-link-inline"
        >
          {resendState === 'sending'
            ? t('resendVerificationSending')
            : t('resendVerificationAction')}
        </button>
      </p>
    );
  };

  const renderSuccessState = (
    icon: React.ReactNode,
    title: string,
    desc: string,
    action: () => void,
    extra?: React.ReactNode
  ) => (
    <div className="auth-success-state">
      <div className="auth-success-icon">{icon}</div>
      <h3 className="auth-success-title">{title}</h3>
      <p className="auth-success-desc">{desc}</p>
      {extra}
      <button onClick={action} className="auth-link-btn">
        <ArrowLeft size={14} />
        {t('backToLogin')}
      </button>
    </div>
  );

  /* ── 2FA screen ────────────────────────────────────────────────────────── */
  const render2FA = () => (
    <div className="auth-2fa">
      <div className="auth-success-icon">
        <KeyRound size={28} />
      </div>
      <h3 className="auth-2fa-title">{t('twoFactorLoginTitle')}</h3>
      <p className="auth-2fa-desc">{t('twoFactorLoginDesc')}</p>

      {error && (
        <div className="auth-error" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handle2FASubmit} className="auth-2fa-form">
        <input
          type="text"
          value={totpCode}
          onChange={(e) =>
            setTotpCode(
              e.target.value
                .replace(/[^0-9A-Fa-f]/g, '')
                .slice(0, 8)
                .toUpperCase()
            )
          }
          placeholder="000000"
          className="auth-totp-input"
          maxLength={8}
          autoFocus
          aria-label={t('auth2faCodeAria')}
        />
        <button type="submit" disabled={loading || totpCode.length < 6} className="auth-submit-btn">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />}
          {t('twoFactorLoginVerify')}
        </button>
      </form>

      <button
        onClick={() => {
          setRequires2FA(false);
          setTotpCode('');
          setError(null);
        }}
        className="auth-link-btn"
      >
        <ArrowLeft size={14} />
        {t('twoFactorLoginBack')}
      </button>
    </div>
  );

  /* ── Main form ─────────────────────────────────────────────────────────── */
  const renderForm = () => (
    <>
      <form onSubmit={handleSubmit} className="auth-form">
        {/* Zodra de mail opnieuw onderweg is, verdwijnt de foutmelding: het
            verzoek is gelukt, en een bevestiging in een rood foutvak leest als
            een tweede probleem (#1403). */}
        {error && resendState !== 'done' && (
          <div className="auth-error animate-shake" role="alert">
            {error}
            {renderResend()}
          </div>
        )}
        {resendState === 'done' && (
          <div className="auth-notice" role="status">
            {t('resendVerificationDone')}
          </div>
        )}

        {/* First name field (register only) */}
        {mode === 'register' && (
          <div className="auth-field">
            <label htmlFor="register-firstname" className="auth-label">
              {t('firstName')}
            </label>
            <div className="auth-input-wrap">
              <User className="auth-input-icon" size={18} />
              <input
                id="register-firstname"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="auth-input"
                placeholder={t('firstNamePlaceholder')}
                maxLength={255}
              />
            </div>
          </div>
        )}

        {/* Email field */}
        <div className="auth-field">
          <Label htmlFor="login-email" required={mode !== 'forgot'}>
            {t('email')}
          </Label>
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('emailPlaceholder')}
            leftIcon={Mail}
            required
            aria-required="true"
          />
        </div>

        {/* Password field */}
        {mode !== 'forgot' && (
          <div className="auth-field">
            <Label htmlFor="login-password" required>
              {t('password')}
            </Label>
            <div className="relative">
              <Input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                leftIcon={Lock}
                className="pr-9"
                required
                aria-required="true"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-pt-forest transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        )}

        {mode === 'forgot' && <p className="auth-hint">{t('forgotPasswordDesc')}</p>}

        {mode === 'login' && (
          <div className="auth-forgot-row">
            <button type="button" onClick={() => switchMode('forgot')} className="auth-forgot-link">
              {t('forgotPassword')}
            </button>
          </div>
        )}

        {mode === 'register' && (
          <label className="auth-tos">
            <input
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              className="auth-tos-checkbox"
              required
              aria-required="true"
            />
            <span>
              {t('tosAcceptLabel').split(t('termsOfService'))[0]}
              <a href="/terms" target="_blank" className="auth-tos-link">
                {t('termsOfService')}
              </a>{' '}
              {t('ogTosAnd')}{' '}
              <a href="/privacy" target="_blank" className="auth-tos-link">
                {t('privacyPolicy')}
              </a>
              .
            </span>
          </label>
        )}

        <Button
          type="submit"
          loading={loading}
          disabled={mode === 'register' && !tosAccepted}
          fullWidth
          size="lg"
          leftIcon={LogIn}
        >
          {mode === 'forgot'
            ? t('forgotPasswordSend')
            : mode === 'login'
              ? t('login')
              : t('register')}
        </Button>
      </form>

      {mode !== 'forgot' && (
        <>
          <div className="auth-divider">
            <span>{t('or')}</span>
          </div>

          <div className="auth-google-wrap">
            {googleClientId ? (
              <div id="google-signin-button" className="auth-google-btn" />
            ) : (
              <button type="button" disabled className="auth-google-fallback">
                <Globe size={18} />
                {t('googleLoginUnavailable')}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );

  /* ── Determine right panel content ─────────────────────────────────────── */
  const renderRightContent = () => {
    if (requires2FA) return render2FA();
    if (forgotPasswordSent)
      return renderSuccessState(
        <Mail size={28} />,
        t('forgotPasswordSent'),
        t('forgotPasswordSentDesc'),
        () => switchMode('login')
      );
    if (registrationSuccess)
      return renderSuccessState(
        <CheckCircle2 size={28} />,
        t('registrationSuccess') || 'Account aangemaakt!',
        t('checkEmailVerification') || 'We hebben een verificatie-email gestuurd.',
        () => switchMode('login'),
        renderResend()
      );
    return renderForm();
  };

  return (
    <div className="auth-screen">
      {/* ═══ LEFT PANEL — Brand / Marketing ═══ */}
      <div className="auth-brand-panel">
        {/* Decorative tree-ring arcs */}
        <svg className="auth-rings" viewBox="0 0 800 800" fill="none" aria-hidden="true">
          <circle cx="400" cy="400" r="160" stroke="var(--gold)" strokeWidth="0.5" opacity="0.15" />
          <circle
            cx="400"
            cy="400"
            r="240"
            stroke="var(--bird-cream)"
            strokeWidth="0.5"
            opacity="0.1"
          />
          <circle
            cx="400"
            cy="400"
            r="320"
            stroke="var(--bird-cream)"
            strokeWidth="0.5"
            opacity="0.07"
          />
          <circle
            cx="400"
            cy="400"
            r="400"
            stroke="var(--bird-cream)"
            strokeWidth="0.5"
            opacity="0.04"
          />
        </svg>

        {/* Subtle ambient glow */}
        <div className="auth-brand-glow" />

        {/* Content */}
        <div className="auth-brand-content">
          {/* Logo + Wordmark */}
          <div className="auth-brand-header">
            <PecktreeLogo size={52} />
            <div className="auth-wordmark">
              <span className="auth-wordmark-peck">Peck</span>
              <span className="auth-wordmark-tree">tree</span>
            </div>
          </div>

          {/* Hero copy */}
          <div className="auth-hero">
            <h1 className="auth-hero-title">
              {t('heroTitle1')} <br />
              <span className="auth-hero-accent">{t('heroTitle2')}</span>
            </h1>
            <p className="auth-hero-desc">{t('heroDesc')}</p>
          </div>

          {/* Feature cards */}
          <div className="auth-features">
            {features.map((f, i) => (
              <div key={i} className="auth-feature">
                <div className="auth-feature-icon">
                  <f.icon size={20} />
                </div>
                <div>
                  <h3 className="auth-feature-title">{t(f.titleKey)}</h3>
                  <p className="auth-feature-desc">{t(f.descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="auth-trust-bar">
            <span className="auth-trust-badge">
              <CheckCircle2 size={13} />
              {t('archiveWorthy')}
            </span>
            <span className="auth-trust-badge">
              <CheckCircle2 size={13} />
              {t('euResidentData')}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ RIGHT PANEL — Auth Form ═══ */}
      <div className="auth-form-panel">
        <div className="auth-form-inner">
          {/* Mobile logo */}
          <div className="auth-mobile-header">
            <PecktreeLogo size={36} />
            <div className="auth-wordmark">
              <span className="auth-wordmark-peck" style={{ color: 'var(--forest)' }}>
                Peck
              </span>
              <span className="auth-wordmark-tree">tree</span>
            </div>
          </div>

          {/* Invitation banner */}
          {inviteInfo && (
            <div className="auth-invite-banner">
              <div className="auth-invite-icon">
                <Users size={18} />
              </div>
              <div>
                <p className="auth-invite-label">
                  {t('youveBeenInvited') || "You've been invited"}
                </p>
                <p className="auth-invite-project">{inviteInfo.project_name}</p>
                <p className="auth-invite-meta">
                  {t('invitedBy') || 'Invited by'} <strong>{inviteInfo.inviter_name}</strong>{' '}
                  &middot; {t(inviteInfo.role as 'editor' | 'viewer') || inviteInfo.role}
                </p>
              </div>
            </div>
          )}

          {/* Title + mode switch */}
          <div className="auth-form-header">
            <h2 className="auth-form-title">
              {mode === 'forgot'
                ? t('forgotPasswordTitle')
                : mode === 'login'
                  ? t('welcomeBack')
                  : t('startLegacy')}
            </h2>
            <p className="auth-form-subtitle">
              {mode === 'forgot' ? (
                <button onClick={() => switchMode('login')} className="auth-mode-link">
                  <ArrowLeft size={14} />
                  {t('backToLogin')}
                </button>
              ) : (
                <>
                  {mode === 'login' ? t('noAccount') : t('hasAccount')}
                  <button
                    onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                    className="auth-mode-link"
                  >
                    {mode === 'login' ? t('register') : t('login')}
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Dynamic content area */}
          {renderRightContent()}

          {/* Professional callout */}
          <div className="auth-pro-callout">
            <Crown size={20} className="auth-pro-icon" />
            <div>
              <p className="auth-pro-title">{t('axonProfessional')}</p>
              <p className="auth-pro-desc">{t('axonProfessionalDesc')}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="auth-footer">
          <span>
            {t('needHelp')}{' '}
            {/* #1756 — was een knop zonder onClick; wie niet kan inloggen, kan wel mailen. */}
            <a href="mailto:support@pecktree.nl" className="auth-footer-link">
              {t('contactSupport')}
            </a>
          </span>
          <span className="auth-footer-sep">&middot;</span>
          <a href="/privacy" className="auth-footer-link">
            {t('privacyPolicy')}
          </a>
          <span className="auth-footer-sep">&middot;</span>
          <a href="/terms" className="auth-footer-link">
            {t('termsOfService')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
